export type ShopifyOrderAddress = {
  zip: string;
  city: string;
  name: string;
  phone: string | null;
  company: string | null;
  country: string;
  address1: string;
  address2: string | null;
  latitude: number | null;
  province: string;
  last_name: string;
  longitude: number | null;
  first_name: string;
  country_code: string;
  province_code: string;
};

export enum ShopifyScopes {
  GIFT_CARDS = 'write_gift_cards',
  GIFT_CARD_TRANSACTIONS = 'write_gift_card_transactions',
  DISCOUNTS = 'write_discounts',
  STORE_CREDIT_ACCOUNT_TRANSACTIONS = 'write_store_credit_account_transactions',
  READ_STORE_CREDIT_ACCOUNTS = 'read_store_credit_accounts',
}

/**
 * Shopify reserved metafield namespaces
 * Custom namespaces are allowed but should be filtered out in certain contexts
 */
export enum ShopifyMetafieldNamespace {
  /** Shopify's global namespace - typically filtered out */
  GLOBAL = 'global',
  /** Custom namespace prefix (namespaces starting with 'custom' are merchant-defined) */
  CUSTOM = 'custom',

  SHOPIFY = 'shopify',
}

/**
 * Shopify metafield types
 * @see https://shopify.dev/docs/api/admin-graphql/latest/enums/MetafieldType
 */
export enum ShopifyMetafieldType {
  // Text types
  SINGLE_LINE_TEXT = 'single_line_text_field',
  MULTI_LINE_TEXT = 'multi_line_text_field',
  STRING = 'string',

  // Number types
  NUMBER_INTEGER = 'number_integer',
  NUMBER_DECIMAL = 'number_decimal',

  // Date/Time
  DATE = 'date',
  DATE_TIME = 'date_time',

  // Boolean
  BOOLEAN = 'boolean',

  // Rich content
  JSON = 'json',

  // References
  PRODUCT_REFERENCE = 'product_reference',
  VARIANT_REFERENCE = 'variant_reference',
  PAGE_REFERENCE = 'page_reference',
  FILE_REFERENCE = 'file_reference',

  // Lists
  LIST_SINGLE_LINE_TEXT = 'list.single_line_text_field',
  LIST_PRODUCT_REFERENCE = 'list.product_reference',
  LIST_VARIANT_REFERENCE = 'list.variant_reference',

  // Dimensions
  DIMENSION = 'dimension',
  VOLUME = 'volume',
  WEIGHT = 'weight',

  // Money
  MONEY = 'money',

  // Rating
  RATING = 'rating',

  // Color
  COLOR = 'color',

  // URL
  URL = 'url',
}

export interface ResolvedMetaobject {
  id: string;
  handle: string;
  type: string;
  displayName: string;
  fields: { key: string; value: string; type: string }[];
}

export interface ShopifyMetafield {
  id: string;
  namespace: string;
  key: string;
  value: string;
  type: string;
  resolvedValue?: ResolvedMetaobject | ResolvedMetaobject[];
}

export interface ProductMetafields {
  product?: ShopifyMetafield[];
  variant?: ShopifyMetafield[];
}

export interface ShopifyBulkProduct {
  id: string;
  productId: string;
  variantId: string;
  parentId: null;
  name: string;
  title?: string;
  sku: string;
  price: number;
  productImage: string | null;
  featured_image?: { url: string };
  image?: { src: string };
  description: string | null;
  body_html?: string;
  tax: number;
  inventoryType: string;
  inventoryQuantity: number;
  totalInventory?: number;
  inStock: boolean;
  continueSelling?: boolean;
  tracked?: boolean;
  status: string;
  productType: string;
  storeId: string;
  metafields?: ProductMetafields;
  /** The platform product's tags, shared by all its variants. */
  tags?: string[];
  /** The platform's own product type (e.g. Shopify `productType`), not our SIMPLE/GROUPED `productType`. */
  platformProductType?: string | null;
}

// https://shopify.dev/docs/api/admin-graphql/latest/enums/ProductVariantInventoryPolicy
export enum ShopifyInventoryPolicy {
  CONTINUE = 'CONTINUE',
  DENY = 'DENY',
}

export enum BulkOperationStatus {
  CANCELED = 'CANCELED',
  CANCELING = 'CANCELING',
  COMPLETED = 'COMPLETED',
  CREATED = 'CREATED',
  EXPIRED = 'EXPIRED',
  FAILED = 'FAILED',
  RUNNING = 'RUNNING',
}

export interface ShopifyBulkItemBase {
  id: string;
  __parentId?: string;
}

export interface ShopifyBulkProductItem extends ShopifyBulkItemBase {
  legacyResourceId: string;
  title: string;
  status?: string;
  tags?: string[];
  productType?: string | null;
  featuredImage?: {
    url: string;
  } | null;
}

export interface ShopifyBulkProductVariantItem extends ShopifyBulkItemBase {
  legacyResourceId: string;
  title: string;
  sku?: string;
  price: string;
  inventoryQuantity?: number;
  /** CONTINUE means the shop sells past zero, so a negative quantity is still available. */
  inventoryPolicy?: ShopifyInventoryPolicy;
  /** Untracked variants have no meaningful quantity, so they are always available. */
  inventoryItem?: { tracked?: boolean };
  __parentId: string;
}

export interface ShopifyBulkMetafieldItem extends ShopifyBulkItemBase {
  namespace: string;
  key: string;
  value: string;
  type: string;
  __parentId: string;
}

export interface ShopifyBulkMetaobjectItem extends ShopifyBulkItemBase {
  handle: string;
  displayName: string;
  type: string;
  __parentId: string;
}

export type ShopifyBulkJsonlItem =
  | ShopifyBulkProductItem
  | ShopifyBulkProductVariantItem
  | ShopifyBulkMetafieldItem
  | ShopifyBulkMetaobjectItem;

  export enum TransactionKind {
    AUTHORIZATION = 'AUTHORIZATION',
    CAPTURE = 'CAPTURE',
    SALE = 'SALE',
    REFUND = 'REFUND',
    VOID = 'VOID',
  }

export enum ShopifyDisputeType {
  INQUIRY = 'inquiry',
  CHARGEBACK = 'chargeback',
}

export enum ShopifyDisputeStatus {
  NEEDS_RESPONSE = 'needs_response',
  UNDER_REVIEW = 'under_review',
  CHARGE_REFUNDED = 'charge_refunded',
  ACCEPTED = 'accepted',
  WON = 'won',
  LOST = 'lost',
}

/** Where a chargeback answer came from, so a coarse answer is not mistaken for a confirmed one. */
export enum ChargebackSource {
  /** Read live from the platform; `disputes` carries the detail. */
  LIVE = 'live',
  /** Only the stored flag is known — the live read was skipped or unavailable. */
  STORED_FLAG = 'stored_flag',
  /** The platform has no dispute concept for this order, so there is nothing to read. */
  NOT_APPLICABLE = 'not_applicable',
}

/** A dispute as returned by Shopify's `Order.disputes`. */
export interface OrderDispute {
  id: string;
  status: ShopifyDisputeStatus;
  initiatedAs: ShopifyDisputeType;
}
